import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { getLiveBoard, LiveBoard, liveBoardsAtom } from '../services/sessionLive.service';

// people check the page right after the organizer moves on, so keep the lag short; the board is a tiny response
const POLL_MS = 5000;
// a hidden or covered window still polls, more slowly, so it isn't stale when it comes back (and catches up on
// visibilitychange). 15 s, not a minute: people leave the page in the background during presentations and the stage
// moves every few minutes (run025: three surfaces a minute behind). Cached board reads cost ~0.15 ms.
const HIDDEN_POLL_MS = 15000;
// spread polls out, so hundreds of pages opened at the same moment don't keep hitting the server in lockstep
const jittered = (ms: number) => ms * (0.8 + Math.random() * 0.4);

// one poller per hackathon no matter how many components watch it; stops when nobody does
const pollers = new Map<string, { subscribers: number, timer: number, restart: () => void }>();

const fetchBoard = (hackathonId: string) => getLiveBoard(hackathonId).catch(err => console.error(err));

const subscribe = (hackathonId: string) => {
    const existing = pollers.get(hackathonId);
    if (existing) {
        existing.subscribers++;
        return;
    }
    const poller = { subscribers: 1, timer: 0, restart: () => { } };
    const schedule = () => {
        window.clearTimeout(poller.timer);
        poller.timer = window.setTimeout(() => {
            fetchBoard(hackathonId).finally(() => {
                // unsubscribed meanwhile: stop
                if (pollers.get(hackathonId) === poller) {
                    schedule();
                }
            });
        }, jittered(document.visibilityState === 'visible' ? POLL_MS : HIDDEN_POLL_MS));
    };
    // fetch now and start the cycle over, e.g. when the tab comes back after slow hidden polling
    let lastRestart = 0;
    poller.restart = () => {
        // every watching component listens for focus; one fetch is enough
        if (Date.now() - lastRestart < 1000) {
            return;
        }
        lastRestart = Date.now();
        fetchBoard(hackathonId);
        schedule();
    };
    pollers.set(hackathonId, poller);
    poller.restart();
}

const unsubscribe = (hackathonId: string) => {
    const existing = pollers.get(hackathonId);
    if (!existing) {
        return;
    }
    existing.subscribers--;
    if (existing.subscribers <= 0) {
        window.clearTimeout(existing.timer);
        pollers.delete(hackathonId);
    }
}

/**
 * The hackathon's live board: hackathon-wide voting and each room's active team, presented teams and
 * voting switch. Polled about every 5s while visible (15s while hidden), and when the tab comes back.
 */
export const useLiveBoard = (hackathonId: string | undefined): LiveBoard => {
    const all = useAtomValue(liveBoardsAtom);

    useEffect(() => {
        if (!hackathonId) {
            return;
        }
        subscribe(hackathonId);
        const onFocus = () => pollers.get(hackathonId)?.restart();
        const onVisible = () => document.visibilityState === 'visible' && pollers.get(hackathonId)?.restart();
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            unsubscribe(hackathonId);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [hackathonId]);

    return (hackathonId && all[hackathonId]) || { votingPeriodOn: false, sessions: [] };
}

/**
 * Each room's live state (see useLiveBoard).
 */
export const useLiveSessions = (hackathonId: string | undefined) => useLiveBoard(hackathonId).sessions;
